import { useMemo } from 'react';

import type { DataTablePagination, SimplePagination } from '../components/composed/data-table/data-table';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UseDataTablePaginationLabels {
  nextPage: string;
  pageLabel: (current: number, total: number) => string;
  /** Label rendered next to the page-size selector — omit when the table has no selector. */
  pageSizeLabel?: string;
  previousPage: string;
  showingText: (start: number, end: number, total: number) => string;
}

interface UseDataTablePaginationOptions {
  labels: UseDataTablePaginationLabels;
  pagination: SimplePagination;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Converts a SimplePagination object + i18n label functions into the
 * DataTablePagination shape expected by <DataTable />.
 *
 * Eliminates the identical useMemo<DataTablePagination> block duplicated
 * across all table components.
 */
export function useDataTablePagination({ labels, pagination }: UseDataTablePaginationOptions): DataTablePagination {
  const { nextPage, pageLabel, pageSizeLabel, previousPage, showingText } = labels;
  const { currentPage, onPageChange, onPageSizeChange, pageSize, pageSizeOptions, totalItems, totalPages } = pagination;

  return useMemo<DataTablePagination>(
    () => ({
      currentPage,
      nextLabel: nextPage,
      onPageChange,
      onPageSizeChange,
      pageLabel: pageLabel(currentPage, totalPages),
      pageSize,
      pageSizeLabel,
      pageSizeOptions,
      previousLabel: previousPage,
      showingText,
      totalItems,
      totalPages,
    }),
    [
      currentPage,
      nextPage,
      onPageChange,
      onPageSizeChange,
      pageLabel,
      pageSize,
      pageSizeLabel,
      pageSizeOptions,
      previousPage,
      showingText,
      totalItems,
      totalPages,
    ],
  );
}
